import React, { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote,
  Send, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw, Inbox, Search,
  CheckCircle2, XCircle, HelpCircle, FileCheck, Heart,
} from "lucide-react";

/* Radha — Module 2: Dispute Resolution + Customer Success (self-contained preview)
   Seeded to match 002_disputes.sql. In production: pages/Disputes.tsx + components/*. */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, Menlo, monospace";
const sans = "'Inter', system-ui, sans-serif";

function formatINR(n) {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n), s = n < 0 ? "-" : "";
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${s}₹${Math.round(a / 1e5)}L`;
  return `${s}₹${a.toLocaleString("en-IN")}`;
}

const VERDICT = {
  valid: { c: C.success, label: "valid", Icon: CheckCircle2 },
  invalid: { c: C.danger, label: "invalid", Icon: XCircle },
  partially_valid: { c: C.warning, label: "partial", Icon: FileCheck },
  unclear: { c: C.ai, label: "unclear", Icon: HelpCircle },
};
const BAND = { healthy: C.success, watch: C.primary, at_risk: C.warning, critical: C.danger };

/* Seed: 8 disputes from 002_disputes.sql */
const DISPUTES = [
  { cust: "Reliance Industries Ltd", inv: "INV-2026-0142", amt: 2400000, disp: 400000, cat: "pricing", status: "resolved", verdict: "invalid", conf: 0.91 },
  { cust: "Tata Consultancy Services", inv: "INV-2026-0188", amt: 1850000, disp: 1850000, cat: "duplicate", status: "resolved", verdict: "invalid", conf: 0.95 },
  { cust: "Infosys Ltd", inv: "INV-2026-0205", amt: 3200000, disp: 800000, cat: "service_level", status: "resolved", verdict: "partially_valid", conf: 0.72 },
  { cust: "Mahindra & Mahindra", inv: "INV-2026-0231", amt: 1200000, disp: 1200000, cat: "quality", status: "under_review", verdict: "unclear", conf: 0.41 },
  { cust: "Wipro Ltd", inv: "INV-2026-0240", amt: 980000, disp: 120000, cat: "tax_gst", status: "resolved", verdict: "invalid", conf: 0.88 },
  { cust: "L&T Finance Holdings", inv: "INV-2026-0258", amt: 4100000, disp: 600000, cat: "billing_error", status: "open", verdict: null, conf: null },
  { cust: "Bajaj Finance Ltd", inv: "INV-2026-0263", amt: 1500000, disp: 300000, cat: "delivery", status: "under_review", verdict: null, conf: null },
  { cust: "HDFC Bank Ltd", inv: "INV-2026-0271", amt: 2750000, disp: 2750000, cat: "pricing", status: "escalated", verdict: null, conf: null },
];

/* Evidence + draft for the open AI proposal (Mahindra, unclear) */
const PROPOSAL = {
  cust: "Mahindra & Mahindra",
  verdict: "unclear",
  conf: 0.41,
  rationale: "The only evidence is a customer email asserting a quality problem with no inspection report or photographs. Acceptance criteria cannot be evaluated without documentation, so the claim can neither be substantiated nor dismissed.",
  evidence: [
    { type: "email", summary: "Customer email describes a quality issue but provides no inspection report or photos.", ref: "Email 2026-05-05", forCust: true },
  ],
  draft: "Dear Mahindra & Mahindra team,\n\nThank you for raising this dispute. To assess the quality concern fairly, could you please share the inspection report and photographs referenced in your note? We will review against the agreed acceptance criteria and respond within our SLA.\n\nRegards,\nAccounts Receivable, Bharat Industries Ltd",
};

const HEALTH = [
  { cust: "HDFC Bank Ltd", score: 38, churn: 0.69, band: "critical", open: 1 },
  { cust: "L&T Finance Holdings", score: 48, churn: 0.51, band: "at_risk", open: 1 },
  { cust: "Mahindra & Mahindra", score: 55, churn: 0.44, band: "at_risk", open: 1 },
  { cust: "Bajaj Finance Ltd", score: 60, churn: 0.35, band: "watch", open: 1 },
  { cust: "Infosys Ltd", score: 64, churn: 0.31, band: "watch", open: 1 },
  { cust: "Wipro Ltd", score: 71, churn: 0.22, band: "watch", open: 0 },
  { cust: "Tata Consultancy Services", score: 82, churn: 0.12, band: "healthy", open: 0 },
  { cust: "Reliance Industries Ltd", score: 88, churn: 0.08, band: "healthy", open: 0 },
];

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall },
  { code: "M2", name: "Dispute Resolution", icon: Scale, active: true },
  { code: "M3", name: "Contract MIS", icon: FileText },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp },
  { code: "M5", name: "Invoicing Engine", icon: Receipt },
  { code: "M6", name: "Payment Reminders", icon: Send },
  { code: "M7", name: "Cash Application", icon: Banknote },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid },
];

function Badge({ children, color }) {
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color, background: `${color}14`, border: `0.5px solid ${color}33`, borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap" }}>{children}</span>;
}
function VerdictPill({ v }) {
  if (!v) return <span style={{ fontFamily: mono, fontSize: 9, color: C.muted }}>—</span>;
  const { c, label, Icon } = VERDICT[v];
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color: c, background: `${c}14`, border: `0.5px solid ${c}33`, borderRadius: 20, padding: "3px 8px" }}><Icon size={10} />{label}</span>;
}
function KPICard({ label, value, sub, accent }) {
  return <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 150 }}>
    <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 26, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
  </div>;
}

export default function Disputes() {
  const [view, setView] = useState("data");
  const [drawer, setDrawer] = useState(false);
  const [query, setQuery] = useState("");

  const rows = useMemo(() => DISPUTES.filter(d => d.cust.toLowerCase().includes(query.toLowerCase())), [query]);
  const kpis = useMemo(() => ({
    open: DISPUTES.filter(d => !d.verdict).length,
    disputed: DISPUTES.reduce((s, d) => s + d.disp, 0),
    resolved: DISPUTES.filter(d => d.status === "resolved").length,
    atRisk: HEALTH.filter(h => h.band === "at_risk" || h.band === "critical").length,
  }), []);
  const verdictDist = useMemo(() => {
    const counts = { valid: 0, invalid: 0, partially_valid: 0, unclear: 0 };
    DISPUTES.forEach(d => d.verdict && counts[d.verdict]++);
    return Object.entries(counts).map(([k, v]) => ({ name: VERDICT[k].label, value: v, color: VERDICT[k].c }));
  }, []);

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .rrow:hover{background:${C.bg}!important}.nav:hover{background:#ffffff14}`}</style>

      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {MODULES.map(m => { const Icon = m.icon; return (
          <div key={m.code} className="nav" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: m.active ? C.primary : "transparent", color: m.active ? "#fff" : "#AEB4C0" }}>
            <Icon size={15} /><span style={{ fontSize: 12.5 }}>{m.name}</span>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
          </div>); })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M2 · DISPUTE RESOLUTION + CUSTOMER SUCCESS</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Disputes & Customer Health</h1>
          </div>
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer", background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted }}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Open Disputes" value={kpis.open} sub="awaiting verdict" accent={C.warning} />
          <KPICard label="Amount Disputed" value={formatINR(kpis.disputed)} sub="across 8 disputes" />
          <KPICard label="Resolved" value={kpis.resolved} sub="this period" accent={C.success} />
          <KPICard label="Churn-Risk Customers" value={kpis.atRisk} sub="at-risk / critical" accent={C.danger} />
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 540px", minWidth: 320 }}>
            {/* EF-CORE evidence-citation panel */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · VERDICT PROPOSAL</span>
                <span style={{ marginLeft: "auto" }}><VerdictPill v={PROPOSAL.verdict} /></span>
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 500, marginTop: 10 }}>{PROPOSAL.cust} · {formatINR(1200000)} disputed</div>
              <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.5, color: "#2A2A2E" }}>{PROPOSAL.rationale}</p>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.ai, marginTop: 12, marginBottom: 6 }}>Cited evidence</div>
              {PROPOSAL.evidence.map((e, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#fff", border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", marginBottom: 6 }}>
                  <FileText size={13} color={C.muted} style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12 }}>{e.summary}</div>
                    <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, marginTop: 3 }}>{e.type} · {e.ref}</div>
                  </div>
                </div>
              ))}
              <button onClick={() => setDrawer(true)} style={{ marginTop: 10, background: C.ai, color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Review verdict & draft reply</button>
            </div>

            {/* Disputes table */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: `0.5px solid ${C.border}` }}>
                <Search size={14} color={C.muted} />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter disputes…" style={{ border: "none", outline: "none", fontSize: 13, flex: 1, background: "transparent", color: C.text }} />
                <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{rows.length} disputes</span>
              </div>
              {view === "loading" && <Skeleton />}
              {view === "error" && <ErrorState />}
              {view === "empty" && <EmptyState />}
              {view === "data" && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Customer / Invoice", "Disputed", "Category", "Verdict", "Status"].map((h, i) => (
                    <th key={h} style={{ textAlign: i === 1 ? "right" : "left", fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, fontWeight: 500, padding: "10px 14px", borderBottom: `0.5px solid ${C.border}` }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {rows.map((d, i) => (
                      <tr key={i} className="rrow" onClick={() => setDrawer(true)} style={{ cursor: "pointer", borderBottom: `0.5px solid ${C.border}` }}>
                        <td style={{ padding: "11px 14px" }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{d.cust}</div>
                          <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, marginTop: 3 }}>{d.inv} · {formatINR(d.amt)} invoice</div>
                        </td>
                        <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 13 }}>{formatINR(d.disp)}</td>
                        <td style={{ padding: "11px 14px", fontSize: 12, color: C.muted }}>{d.cat.replace("_", " ")}</td>
                        <td style={{ padding: "11px 14px" }}><VerdictPill v={d.verdict} /></td>
                        <td style={{ padding: "11px 14px", fontSize: 12 }}>{d.status.replace("_", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Verdict distribution */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "14px 14px 6px", marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, marginBottom: 8 }}>Adjudicated verdict mix</div>
              <ResponsiveContainer width="100%" height={130}>
                <BarChart data={verdictDist} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: C.bg }} contentStyle={{ borderRadius: 8, border: `0.5px solid ${C.border}`, fontSize: 12 }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>{verdictDist.map((e, i) => <Cell key={i} fill={e.color} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Customer health rail */}
          <div style={{ flex: "0 0 250px", minWidth: 210 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <Heart size={13} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Customer health · churn risk</span>
              </div>
              {HEALTH.map((h, i) => (
                <div key={i} style={{ marginTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12 }}>{h.cust}</span>
                    <Badge color={BAND[h.band]}>{h.band.replace("_", " ")}</Badge>
                  </div>
                  <div style={{ height: 5, background: C.border, borderRadius: 3, marginTop: 5, overflow: "hidden" }}>
                    <div style={{ width: `${h.score}%`, height: "100%", background: BAND[h.band] }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Compliance</div>
              {[["Append-only audit", "every verdict logged"], ["DPDP PAN masking", "XXXXX####X in logs"], ["Evidence citation", "verdicts cite sources"]].map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12 }}>
                  <ShieldCheck size={15} color={C.success} style={{ marginTop: 1, flexShrink: 0 }} />
                  <div><div style={{ fontSize: 12.5, fontWeight: 500 }}>{r[0]}</div><div style={{ fontSize: 11, color: C.muted }}>{r[1]}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {drawer && <VerdictDrawer onClose={() => setDrawer(false)} />}
    </div>
  );
}

function Skeleton() {
  return <div style={{ padding: 14 }}>{[...Array(5)].map((_, i) => <div key={i} style={{ height: 18, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />)}<style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style></div>;
}
function ErrorState() {
  return <div style={{ padding: 40, textAlign: "center" }}><AlertTriangle size={26} color={C.danger} /><div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load disputes</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>The disputes service did not respond.</div><button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}><RefreshCw size={13} /> Retry</button></div>;
}
function EmptyState() {
  return <div style={{ padding: 44, textAlign: "center" }}><Inbox size={26} color={C.muted} /><div style={{ marginTop: 10, fontWeight: 500 }}>No open disputes</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Disputes raised by customers will appear here.</div></div>;
}

function VerdictDrawer({ onClose }) {
  const [decided, setDecided] = useState(null);
  const { c } = VERDICT[PROPOSAL.verdict];
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 420, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Verdict proposal</div>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <h3 style={{ margin: "8px 0 2px", fontSize: 17 }}>{PROPOSAL.cust}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
          <VerdictPill v={PROPOSAL.verdict} />
          <span style={{ fontFamily: mono, fontSize: 11, color: C.muted }}>confidence {PROPOSAL.conf}</span>
        </div>

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Rationale</div>
        <p style={{ fontSize: 13, lineHeight: 1.55, marginTop: 6 }}>{PROPOSAL.rationale}</p>

        <div style={{ marginTop: 14, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Cited evidence</div>
        {PROPOSAL.evidence.map((e, i) => (
          <div key={i} style={{ background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "9px 11px", marginTop: 7 }}>
            <div style={{ fontSize: 12 }}>{e.summary}</div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, marginTop: 3 }}>{e.type} · {e.ref} · {e.forCust ? "favours customer" : "favours us"}</div>
          </div>
        ))}

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Draft customer reply</div>
        <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 8, padding: 12, marginTop: 7, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap", color: "#2A2A2E" }}>{PROPOSAL.draft}</div>

        {decided ? (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, background: decided === "approved" ? "#E9F7F1" : "#FBE9E7", border: `0.5px solid ${(decided === "approved" ? C.success : C.danger)}33`, borderRadius: 9, padding: 12, fontSize: 13, color: decided === "approved" ? C.success : C.danger }}>
            {decided === "approved" ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
            Verdict {decided}. Applied to the dispute and written to the audit log.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
            <button onClick={() => setDecided("approved")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: C.success, color: "#fff", border: "none", borderRadius: 9, padding: "11px 0", fontSize: 13, fontWeight: 500, cursor: "pointer" }}><CheckCircle2 size={14} /> Approve verdict</button>
            <button onClick={() => setDecided("rejected")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "transparent", color: C.danger, border: `0.5px solid ${C.danger}55`, borderRadius: 9, padding: "11px 0", fontSize: 13, cursor: "pointer" }}><XCircle size={14} /> Reject</button>
          </div>
        )}
      </div>
    </div>
  );
}
